import { iconDoc2aVia } from '@bsds-lib-component/icons/icons';
import { BsdsCard, BsdsIcon, BsdsInput, BsdsModal } from '@bsds-lib-component/react';
import React, { useId, useState, useCallback, useMemo } from 'react';

import { SystemMessage, useSystemContainer, Visibility, Dropdown, Pagination } from '../../../components';
import Button from '../../../components/button/Button';
import DigitalInvoincingTable from '../../../components/table/previdencia/DigitalInvoincingTable';
import { useDigitalInvoicing } from '../../../hooks/previdencia/useDigitalInvoicing';
import { useSystemMessage } from '../../../hooks/useSystemMessage';
import type { PaymentListRequest } from '../../../types/previdencia/previdenciaType';
import type { InvoiceHistoryItem } from '../../../types/previdencia/table/invoiceHistoryTableType';

// Helper function to format date from yyyy-MM-dd to dd/MM/yyyy
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

// Helper function to format billing month from yyyy-MM-dd to MM/yyyy
const formatBillingMonth = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const [year, month] = dateString.split('-');
  return `${month}/${year}`;
};

// Helper function to format status name (remove underscores and capitalize properly)
const formatStatus = (status: string): string => {
  // First, map known status values (exact match)
  const statusMap: Record<string, string> = {
    EM_DIA: 'Em dia',
    PENDENTE: 'Pendente',
    VENCIDO: 'Vencido',
    CANCELADO: 'Cancelado',
    EM_ATRASO: 'Atrasado',
    ATENCAO: 'Atenção',
    PAGO: 'Pago',
  };

  // Normalize status to uppercase for comparison
  const normalizedStatus = status.toUpperCase().trim();

  // If status is in the map, return the mapped value
  if (statusMap[normalizedStatus]) {
    return statusMap[normalizedStatus];
  }

  // If status contains underscores, replace with spaces and capitalize each word
  if (status.includes('_')) {
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  // If status already has spaces, just capitalize each word
  if (status.includes(' ')) {
    return status
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  // Otherwise, just capitalize first letter
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const DigitalInvoincingTab: React.FC = () => {
  const { showMessage, message, type, show } = useSystemMessage();
  const { setLoading } = useSystemContainer();
  const mainContentId = useId();
  const searchFormId = useId();
  const resultsId = useId();
  const emptyStateId = useId();
  const messageId = useId();
  const modalId = useId();

  const selectedCompany = { id: '11' };
  const companyId = selectedCompany?.id?.toString() || '';

  // Form states
  const [subCompanyId, setSubCompanyId] = useState('');
  const [emissionFrom, setEmissionFrom] = useState('');
  const [emissionTo, setEmissionTo] = useState('');

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Use the hook for all data and request logic
  const {
    payments,
    isLoadingPayments,
    currentPage,
    totalPages,
    totalElements,
    subCompanies,
    isLoadingSubCompanies,
    paymentDetail,
    hasSearchResults,
    hasSearched,
    searchPayments,
    changePage,
    getPaymentDetail,
    clearPaymentDetail,
  } = useDigitalInvoicing(companyId);

  // Set default subcompany when subcompanies are loaded
  React.useEffect(() => {
    if (subCompanies.length > 0 && !subCompanyId) {
      setSubCompanyId(subCompanies[0].id.toString());
    } else if (subCompanies.length === 0) {
      setSubCompanyId('');
    }
  }, [subCompanies, subCompanyId]);

  // Convert subcompanies to dropdown options
  const subCompanyOptions = subCompanies.map((subCompany) => ({
    label: subCompany.name,
    value: subCompany.id.toString(),
  }));

  const hasRequiredFields = companyId && subCompanyId;

  // Validate dates: both must be filled and emissionFrom must be less than emissionTo
  const areDatesValid = useMemo(() => {
    // Both dates must be filled
    if (!emissionFrom || !emissionTo) {
      return false;
    }
    // emissionFrom must be less than emissionTo
    const startDate = new Date(emissionFrom);
    const endDate = new Date(emissionTo);
    return startDate < endDate;
  }, [emissionFrom, emissionTo]);

  const isSearchDisabled =
    isLoadingPayments ||
    isLoadingSubCompanies ||
    !hasRequiredFields ||
    !areDatesValid;

  const handleSearch = useCallback(async () => {
    if (!companyId) {
      showMessage('Por favor, selecione uma empresa', 'warning');
      return;
    }

    if (!subCompanyId) {
      showMessage('Por favor, selecione uma sucursal', 'warning');
      return;
    }

    setLoading(true);

    try {
      const searchParams: PaymentListRequest = {
        companyId,
        subCompanyId,
        emissionFrom: emissionFrom || undefined,
        emissionTo: emissionTo || undefined,
      };

      await searchPayments(searchParams);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar pagamentos';
      showMessage(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [companyId, subCompanyId, emissionFrom, emissionTo, searchPayments, showMessage, setLoading]);

  const handlePageChange = useCallback(
    async (page: number) => {
      setLoading(true);

      try {
        await changePage(page);

        // Scroll to top of results when page changes
        const resultsElement = document.getElementById(resultsId);
        if (resultsElement) {
          resultsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar página';
        showMessage(errorMessage, 'error');
      } finally {
        setLoading(false);
      }
    },
    [changePage, showMessage, setLoading, resultsId]
  );

  const handleRowClick = useCallback(
    async (invoice: InvoiceHistoryItem) => {
      setLoading(true);

      try {
        // Convert invoice id to number (invoiceId)
        const invoiceId = typeof invoice.id === 'string' ? parseInt(invoice.id, 11) : invoice.id;

        // Promise.all to ensure minimum time of 1500ms and make the request
        await Promise.all([
          getPaymentDetail(invoiceId),
          new Promise(resolve => setTimeout(resolve, 100))
        ]);

        // If we got here, the request was successful
        setIsModalOpen(true);
      } catch (err) {
        console.error('Error loading payment detail:', err);
        // Shows error message and does not open modal
        showMessage('Não foi possível carregar os detalhes do pagamento. Tente novamente.', 'error');
      } finally {
        setLoading(false);
      }
    },
    [getPaymentDetail, showMessage, setLoading]
  );

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    clearPaymentDetail();
  }, [clearPaymentDetail]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const getStatusBadgeClass = (status: string, origin?: string) => {
    switch (status) {
      case 'Pago':
        return origin === 'table' ? 'bg-green-100 text-green-800' : 'text-green-800';
      case 'Pendente':
        return origin === 'table' ? 'bg-yellow-100 text-yellow-800' : 'text-yellow-800';
      case 'Vencido':
        return origin === 'table' ? 'bg-red-100 text-red-800' : 'text-red-800';
      case 'Cancelado':
        return origin === 'table' ? 'bg-gray-100 text-gray-800' : 'text-gray-800';
      case 'Em dia':
        return origin === 'table' ? 'bg-green-100 text-green-600' : 'text-green-600';
      case 'Atenção':
        return origin === 'table' ? 'bg-yellow-100 text-yellow-800' : 'text-yellow-800';
      case 'Atrasado':
        return origin === 'table' ? 'bg-red-100 text-red-800' : 'text-red-800';
      default:
        return origin === 'table' ? 'bg-gray-100 text-gray-800' : 'text-gray-800';
    }
  };

  // Convert payments to InvoiceHistoryItem format
  const invoiceItems: InvoiceHistoryItem[] = payments.map((payment) => ({
    id: payment.id,
    billingMonth: payment.billingMonth,
    contractNumber: payment.contractNumber,
    type: payment.type,
    dueDate: payment.dueDate,
    amount: payment.totalValue,
    status: formatStatus(payment.status),
  }));

  return (
    <div
      className="bsds-width-100 bsds-mt-12 bsds-p-8"
      role="main"
      aria-busy={isLoadingPayments}
      aria-label="Faturamento Digital"
      id={mainContentId}
    >
      {/* System Message */}
      <Visibility isActive={show}>
        <div id={messageId} className="bsds-mb-16" role="alert" aria-live="polite">
          <SystemMessage message={message} type={type} />
        </div>
      </Visibility>

      {/* Search Form */}
      <div
        id={searchFormId}
        className="bsds-mb-24"
        role="region"
        aria-label="Formulário de busca de pagamentos"
      >
        <div className="bsds-mb-16">
          {/* Sucursal - Full width row */}
          <div className="bsds-mb-16">
            <Dropdown
              label="Sucursal"
              placeholder={
                isLoadingSubCompanies
                  ? 'Carregando sucursais...'
                  : subCompanies.length === 0
                    ? 'Nenhuma sucursal disponível'
                    : 'Selecione a sucursal'
              }
              value={subCompanyId}
              onChange={setSubCompanyId}
              options={subCompanyOptions}
              disabled={!companyId || isLoadingSubCompanies || subCompanies.length === 0}
              required
            />
          </div>

          <span className="bsds-text-sm text-text-color">Período de Emissão</span>

          {/* Dates row - Data Inicial and Data Final */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bsds-mb-16 bsds-mt-8">
            {/* Date From */}
            <BsdsInput
              value={emissionFrom}
              onInput={(e) => setEmissionFrom((e.target as HTMLInputElement).value)}
              label="Data Inicial"
              placeholder="yyyy-MM-dd"
              type="date"
              color="primary"
              platform="web"
              className="bsds-width-100"
            />

            {/* Date To */}
            <BsdsInput
              value={emissionTo}
              onInput={(e) => setEmissionTo((e.target as HTMLInputElement).value)}
              label="Data Final"
              placeholder="yyyy-MM-dd"
              type="date"
              color="primary"
              platform="web"
              className="bsds-width-100"
            />
          </div>

          {/* Validation message for dates */}
          {emissionFrom && emissionTo && !areDatesValid && (
            <div className="bsds-mb-16 bsds-text-sm text-red-600" role="alert">
              A data inicial deve ser menor que a data final
            </div>
          )}

          {/* Buttons */}
          <div
            className="bsds-display-flex bsds-justify-content-end gap-4"
            role="group"
            aria-label="Ações do formulário"
          >
            <Button
              onClick={handleSearch}
              disabled={isSearchDisabled}
              aria-label="Buscar pagamentos"
            >
              Buscar
            </Button>
          </div>
        </div>
      </div>

      {/* Results Section */}
      <Visibility isActive={hasSearched}>
        <div
          id={resultsId}
          className="bsds-mb-24"
          role="region"
          aria-label="Resultados da busca"
        >

          {/* Table */}
          <Visibility isActive={hasSearchResults}>
            <BsdsCard className="bsds-p-16">
              <div className="bsds-mb-16">
                <DigitalInvoincingTable
                  invoices={invoiceItems}
                  formatCurrency={formatCurrency}
                  formatDate={formatDate}
                  formatBillingMonth={formatBillingMonth}
                  getStatusBadgeClass={getStatusBadgeClass}
                  onRowClick={handleRowClick}
                  emptyMessage="Nenhum pagamento encontrado"
                />
              </div>
            </BsdsCard>

            {/* Pagination */}
            <div className="bsds-display-flex bsds-justify-content-center bsds-mt-16">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                totalItems={totalElements}
                itemLabel="pagamentos"
                aria-label="Paginação de resultados"
                alwaysShow
              />
            </div>
          </Visibility>

          {/* Empty State */}
          <Visibility isActive={!hasSearchResults && hasSearched}>
            <div
              id={emptyStateId}
              className="bsds-text-center bsds-py-24"
              role="status"
              aria-live="polite"
            >
              <p className="bsds-text-text-color">Nenhum pagamento encontrado para os filtros informados</p>
            </div>
          </Visibility>

        </div>
      </Visibility>

      {/* Detail Modal */}
      <BsdsModal
        isOpen={isModalOpen}
        onBsdsModalDidDismiss={handleCloseModal}
        backdropDismiss
        color="secondary"
        canDismiss
        animated
        showBackdrop
        keepContentsMounted
        platform="web"
        aria-labelledby={modalId}
        aria-describedby={`${modalId}-description`}
      >
        <div
          className="modal-content flex flex-col h-screen max-h-screen md:h-[75vh]"
          role="dialog"
          aria-modal="true"
          aria-labelledby={modalId}
        >
          {paymentDetail && (
            <>
              {/* Header fixed */}
              <div className="flex items-center gap-4 p-4 pb-3 flex-shrink-0 border-b border-gray-100">
                <BsdsIcon icon={iconDoc2aVia} color="secondary" className="icon-title" aria-hidden="true" />
                <h6 id={modalId} className="text-lg font-semibold text-text-color">Detalhamento da Fatura</h6>
              </div>

              {/* Scroll content */}
              <div
                id={`${modalId}-description`}
                className="overflow-y-auto custom-scrollbar flex-1 px-4 py-4 min-h-0 pb-38"
                role="region"
                aria-label="Detalhes da fatura"
              >
                <div className="flex flex-col gap-4">
                  <div className="bsds-display-flex bsds-justify-content-between bsds-align-items-center py-2 border-b border-gray-50">
                    <span className="text-sm text-text-color">Mês da Fatura:</span>
                    <span className="text-sm font-normal" aria-label={`Mês da fatura: ${formatBillingMonth(paymentDetail.billingMonth)}`}>
                      {formatBillingMonth(paymentDetail.billingMonth)}
                    </span>
                  </div>

                  <div className="bsds-display-flex bsds-justify-content-between bsds-align-items-center py-2 border-b border-gray-50">
                    <span className="text-sm text-text-color">Número Apólice:</span>
                    <span className="text-sm font-normal" aria-label={`Número da apólice: ${paymentDetail.contractNumber}`}>
                      {paymentDetail.contractNumber}
                    </span>
                  </div>

                  <div className="bsds-display-flex bsds-justify-content-between bsds-align-items-center py-2 border-b border-gray-50">
                    <span className="text-sm text-text-color">Tipo:</span>
                    <span className="text-sm font-normal" aria-label={`Tipo: ${paymentDetail.type}`}>
                      {paymentDetail.type}
                    </span>
                  </div>

                  <div className="bsds-display-flex bsds-justify-content-between bsds-align-items-center py-2 border-b border-gray-50">
                    <span className="text-sm text-text-color">Segurado:</span>
                    <span className="text-sm font-normal" aria-label={`Segurado: ${paymentDetail.insured}`}>
                      {paymentDetail.insured}
                    </span>
                  </div>

                  <div className="bsds-display-flex bsds-justify-content-between bsds-align-items-center py-2 border-b border-gray-50">
                    <span className="text-sm text-text-color">CPF/CNPJ:</span>
                    <span className="text-sm font-normal" aria-label={`CPF ou CNPJ: ${paymentDetail.cpfOrCnpj}`}>
                      {paymentDetail.cpfOrCnpj}
                    </span>
                  </div>

                  <div className="bsds-display-flex bsds-justify-content-between bsds-align-items-center py-2 border-b border-gray-50">
                    <span className="text-sm text-text-color">Data de Emissão:</span>
                    <span className="text-sm font-normal" aria-label={`Data de emissão: ${formatDate(paymentDetail.emissionDate)}`}>
                      {formatDate(paymentDetail.emissionDate)}
                    </span>
                  </div>

                  <div className="bsds-display-flex bsds-justify-content-between bsds-align-items-center py-2 border-b border-gray-50">
                    <span className="text-sm text-text-color">Vencimento:</span>
                    <span className="text-sm font-normal" aria-label={`Vencimento: ${formatDate(paymentDetail.dueDate)}`}>
                      {formatDate(paymentDetail.dueDate)}
                    </span>
                  </div>

                  {paymentDetail.paymentDate && (
                    <div className="bsds-display-flex bsds-justify-content-between bsds-align-items-center py-2 border-b border-gray-50">
                      <span className="text-sm text-text-color">Data de Pagamento:</span>
                      <span className="text-sm font-normal" aria-label={`Data de pagamento: ${formatDate(paymentDetail.paymentDate)}`}>
                        {formatDate(paymentDetail.paymentDate)}
                      </span>
                    </div>
                  )}

                  {paymentDetail.paymentMethod && (
                    <div className="bsds-display-flex bsds-justify-content-between bsds-align-items-center py-2 border-b border-gray-50">
                      <span className="text-sm text-text-color">Método de Pagamento:</span>
                      <span className="text-sm font-normal" aria-label={`Método de pagamento: ${paymentDetail.paymentMethod}`}>
                        {paymentDetail.paymentMethod}
                      </span>
                    </div>
                  )}

                  <div className="bsds-display-flex bsds-justify-content-between bsds-align-items-center py-2 border-b border-gray-50">
                    <span className="text-sm text-text-color">Valor Base:</span>
                    <span className="text-sm font-normal" aria-label={`Valor base: ${formatCurrency(paymentDetail.baseValue)}`}>
                      {formatCurrency(paymentDetail.baseValue)}
                    </span>
                  </div>

                  {paymentDetail.interest > 0 && (
                    <div className="bsds-display-flex bsds-justify-content-between bsds-align-items-center py-2 border-b border-gray-50">
                      <span className="text-sm text-text-color">Juros:</span>
                      <span className="text-sm font-normal text-red-600" aria-label={`Juros: ${formatCurrency(paymentDetail.interest)}`}>
                        +{formatCurrency(paymentDetail.interest)}
                      </span>
                    </div>
                  )}

                  <hr className="modal-divider border-gray-100" />

                  <div className="bsds-display-flex bsds-justify-content-between bsds-align-items-center py-1.5">
                    <span className="text-base font-bold">Valor Total:</span>
                    <span className="text-base font-bold text-text-color" aria-label={`Valor total: ${formatCurrency(paymentDetail.totalValue)}`}>
                      {formatCurrency(paymentDetail.totalValue)}
                    </span>
                  </div>

                  <div className="bsds-display-flex bsds-justify-content-between bsds-align-items-center py-1.5">
                    <span className="text-sm text-text-color">Status:</span>
                    <span className={`text-sm font-bold px-2 py-1 ${getStatusBadgeClass(formatStatus(paymentDetail.status))}`} aria-label={`Status da fatura: ${formatStatus(paymentDetail.status)}`}>
                      {formatStatus(paymentDetail.status)}
                    </span>
                  </div>

                  {paymentDetail.movementType && (
                    <div className="bsds-display-flex bsds-justify-content-between bsds-align-items-center py-2 border-b border-gray-50">
                      <span className="text-sm text-text-color">Tipo de Movimentação:</span>
                      <span className="text-sm font-normal" aria-label={`Tipo de movimentação: ${paymentDetail.movementType}`}>
                        {paymentDetail.movementType}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Fixed button */}
              <div className="p-4 bottom-0 sticky bg-white mx-2">
                <Button
                  type="button"
                  color="secondary"
                  fill="outline"
                  onClick={handleCloseModal}
                  aria-label="Fechar modal de detalhamento da fatura"
                >
                  Fechar
                </Button>
              </div>
            </>
          )}
        </div>
      </BsdsModal>
    </div>
  );
};

export default DigitalInvoincingTab;

